import type { DesignTokens, ThemeRow, ThemeTokens } from "../types/theme";
import { DARK_BASE_TOKENS, LIGHT_BASE_TOKENS } from "../constants/theme-tokens";
import type { TypographyContextValue } from "../types/typography";
import { resolveThemeTokens } from "./theme-resolver";

/**
 * CANONICAL color mode selected by the user. It is ORTHOGONAL to the theme:
 *  - "system": the light/dark variant follows the OS (prefers-color-scheme).
 *  - "light"/"dark": an EXPLICIT override that WINS over the OS.
 * It is persisted per device (localStorage). A custom theme has light and dark
 * palettes; the mode selects WHICH one applies without changing the active theme.
 */
export type ColorMode = "system" | "light" | "dark";

/** Effective variant already resolved (never "system").*/
export type EffectiveMode = "light" | "dark";

/** Sentinel id of the default theme (not custom).*/
export const DEFAULT_THEME_ID = "default";

export interface ResolveThemeInput {
  /**
   * Row for the active global custom theme (with its light/dark pair), or `null`
   * for the DEFAULT theme. A row without an `id` is treated as default.
   */
  themeRow: ThemeRow | null;
  /** Canonical mode selected by the user.*/
  mode: ColorMode;
  /** Whether the OS reports a dark scheme (`prefers-color-scheme: dark`).*/
  systemPrefersDark: boolean;
  isBlurContext?: boolean;
  parentTokens?: ThemeTokens | null;
  userFonts?: TypographyContextValue;
}

export interface ResolveThemeResult {
  /** Applied theme id (`"default"` or the custom theme id). */
  themeId: string;
  /** True when the default theme is applied (not custom).*/
  isDefault: boolean;
  /** Variant actually applied (light or dark).*/
  effectiveMode: EffectiveMode;
  /** Resolved tokens ready to apply to the DOM.*/
  tokens: ThemeTokens;
}

/**
 * Resolves the effective variant: the EXPLICIT mode (light/dark) WINS over the OS.
 * Only "system" follows `prefers-color-scheme`.
 */
export function resolveEffectiveMode(mode: ColorMode, systemPrefersDark: boolean): EffectiveMode {
  if (mode === "light") {
    return "light";
  }
  if (mode === "dark") {
    return "dark";
  }
  return systemPrefersDark ? "dark" : "light";
}

function baseThemeTokens(dark: boolean, isBlur: boolean): ThemeTokens {
  const base: DesignTokens = dark ? DARK_BASE_TOKENS : LIGHT_BASE_TOKENS;
  return { ...base, isDark: dark, isBlur, id: DEFAULT_THEME_ID };
}

/**
 * PURE logic for resolving the theme system (two orthogonal axes):
 *   (a) WHICH theme → `themeRow` (null = default, or a custom theme)
 *   (b) WHICH mode  → `mode` (system/light/dark) → light/dark variant
 *
 * Guaranteed rules (covered by tests):
 *  - The EXPLICIT mode (light/dark) ALWAYS wins over the OS. Only "system" follows the OS.
 *  - A custom theme is PRESERVED when the mode changes; the mode only chooses its
 *    light or dark variant (using the theme's light/dark pair if one exists).
 *  - The default theme is just another selection (themeRow = null).
 */
export function resolveTheme(input: ResolveThemeInput): ResolveThemeResult {
  const { themeRow, mode, systemPrefersDark } = input;
  const isBlurContext = input.isBlurContext ?? true;
  const effectiveMode = resolveEffectiveMode(mode, systemPrefersDark);
  const wantDark = effectiveMode === "dark";

  // ── Default theme (no custom theme; global row without id = default) ──
  if (!themeRow?.id) {
    return {
      themeId: DEFAULT_THEME_ID,
      isDefault: true,
      effectiveMode,
      tokens: baseThemeTokens(wantDark, isBlurContext),
    };
  }

  // ── Custom theme: choose the variant that matches the effective mode ─
  const primaryIsDark = themeRow.is_dark === 1;
  const hasPair = Boolean(themeRow.p_id);
  const pairMatchesMode = hasPair && (themeRow.p_is_dark === 1) === wantDark;
  // The mode can be honored if the primary variant or its pair matches it.
  const canHonorMode = primaryIsDark === wantDark || pairMatchesMode;

  // When the mode can be honored, force follow_device_theme=1 with
  // systemIsDark=wantDark: the resolver then chooses the correct variant (primary or
  // pair) WITHOUT relying on the user's follow-device preference. When it cannot
  // (a single-variant theme forced to the opposite mode), resolve the theme in its
  // own variant (follow_device_theme=0) so isDark is not mixed with colors.
  const tokens = resolveThemeTokens({
    themeResult: { ...themeRow, follow_device_theme: canHonorMode ? 1 : 0 },
    isBlurContext,
    systemIsDark: wantDark,
    parentTokens: input.parentTokens ?? null,
    userFonts: input.userFonts,
  });

  return {
    themeId: tokens.id ?? themeRow.id,
    isDefault: false,
    effectiveMode: tokens.isDark ? "dark" : "light",
    tokens,
  };
}
